import { createFileRoute, notFound, redirect } from '@tanstack/react-router';

import { ArticlePageView } from '@/components/site/pages';
import { articleBodyPlainText } from '@/lib/article-body';
import { articleExternalHref } from '@/lib/article-card';
import { buildNewsArticleJsonLd } from '@/lib/article-json-ld';
import { articleOgImageUrl, seoHead } from '@/lib/seo';
import { retryOnFetchError } from '@/lib/retry-fetch';
import { loadArticlePage } from '@/server/functions/pages';

export const Route = createFileRoute('/article/$id')({
    staleTime: 30_000,
    loader: async ({ params, location }) => {
        const data = await retryOnFetchError(() => loadArticlePage({ data: params.id }));
        if (!data) throw notFound();

        // Legacy URLs were `/article/{number}`; canonical is `/article/art_{number}`
        // (or the current primary key). Permanent redirect preserves SEO equity.
        if (data.needsCanonicalRedirect) {
            const qs = location.searchStr || '';
            throw redirect({
                href: `/article/${encodeURIComponent(data.article.slug)}${qs}`,
                statusCode: 301,
                replace: true,
            });
        }

        const external = articleExternalHref(data.article.redirect);
        if (external) {
            throw redirect({ href: external });
        }
        return data;
    },
    head: ({ loaderData }) => {
        const article = loaderData?.article;
        if (!article) {
            return seoHead({
                title: 'כתבה לא נמצאה',
                description: 'הכתבה המבוקשת לא נמצאה.',
                path: '/',
                noIndex: true,
            });
        }

        const description =
            article.subtitle
            || articleBodyPlainText(article.body).slice(0, 200);

        const categoryNames = article.categories.map((c) => c.name).filter(Boolean);
        const articlePath = `/article/${encodeURIComponent(article.slug)}`;

        return seoHead({
            title: article.title,
            description,
            path: articlePath,
            canonicalUrl: article.canonical?.trim() || undefined,
            type: 'article',
            publishedTime: article.date,
            modifiedTime: article.$updatedAt,
            image: articleOgImageUrl(article.coverOgUrl),
            imageAlt: article.title,
            keywords: categoryNames.length > 0
                ? [...categoryNames, 'אשקלון', 'חדשות אשקלון'].join(', ')
                : undefined,
            authors: article.authors.map((a) => a.name).filter(Boolean),
            section: article.categories[0]?.name,
            jsonLd: buildNewsArticleJsonLd(article),
        });
    },
    component: ArticlePage,
});

function ArticlePage() {
    const data = Route.useLoaderData();
    return (
        <ArticlePageView
            article={data.article}
            related={data.related}
            latest={data.latest}
            inlineAds={data.inlineAds}
            sidebarAds={data.sidebarAds}
        />
    );
}
